import { PrefillableHubspotFields } from '@/constants/hubspot';
import { API_HUBSPOT_IDENTIFY } from '@/constants/routes';
import { createLogger } from '@/lib/debug';
import { poll } from '@/lib/utils';
import { cloneDeep } from 'lodash';
import { createContext, useCallback, useEffect, useState } from 'react';
import { useLocalstorageState } from 'rooks';

const logger = createLogger('user context');

export const UserContext = createContext<UserContext>({
  userData: {},
  setUserData: () => {},
  getTempUserData: () => ({}),
  setTempUserData: () => {},
});

export const useUserContext = (): UserContext => {
  const [properties, setProperties] = useLocalstorageState<UserProperties>('user-data', {});
  const [tempProperties, setTempProperties] = useState<UserProperties>({});

  const setUserData = (newProps: Partial<UserProperties>) =>
    setProperties((prevProps) => ({ ...prevProps, ...newProps }));

  useEffect(() => {
    if (properties.enrichedFromHubspot) return;

    const urlParams = new URLSearchParams(window.location.search);

    if (!urlParams.has('_hsenc')) return;

    logger.info('Attempting to enrich from Hubspot');

    poll(queryHubspot, {
      maxAttempts: 3,
      delay: (attempt) => attempt * 1000,
      initialDelay: 1500,
    })
      .then((data) => {
        if (!Object.keys(data).length) {
          return void logger.warn('No Hubspot enrichment data was returned');
        }

        logger.success('Enrichment data from Hubspot returned', data);

        setUserData({ ...data, enrichedFromHubspot: true });
      })
      .catch(() => {
        logger.error('Could not enrich from Hubspot');
      });
  }, []);

  const getTempUserData = useCallback(() => {
    const data = cloneDeep(tempProperties);

    setTempProperties({});

    return data;
  }, [tempProperties]);

  const setTempUserData = (tempData: Partial<UserProperties>) => setTempProperties(tempData);

  return {
    userData: properties ?? {},
    setUserData,
    getTempUserData,
    setTempUserData,
  };
};

async function queryHubspot() {
  const response = await fetch(API_HUBSPOT_IDENTIFY);

  if (response.status === 404 || response.status === 204) {
    // No Hubspot enrichment data was returned
    return {};
  }

  if (!response.ok) {
    // Try again
    throw new Error();
  }

  return (await response.json()) as UserProperties;
}

type UserProperties = Partial<Record<(typeof PrefillableHubspotFields)[number], any>> & Record<string, any>;

export type UserContext = {
  userData: UserProperties;
  setUserData: (data: Partial<UserProperties>) => void;
  getTempUserData: () => Partial<UserProperties>;
  setTempUserData: (data: Partial<UserProperties>) => void;
};
